import { ApiError, handleApiError } from '@/handleApiError'
import helpers from '@/modules/message/helpers'
import api from '@/modules/quote/api'
import useQuote from '@/modules/quote/useQuote'
import { t } from '@/plugins/i18n.ts'
import router from '@/router'
import {
  QuoteRequest,
  QuoteRequestItemDto,
  QuoteRequestPatch,
  ShortQuoteRequestItem
} from '@/types/index'
import { defineStore } from 'pinia'

interface QuoteRequestState {
  quoteRequest: QuoteRequest | undefined
  loading: boolean
  rejecting: boolean
  printing: boolean
  creating: boolean
  requesting: boolean
  deleting: boolean
  updating: boolean
}

const initialState = (): QuoteRequestState => {
  return {
    quoteRequest: undefined,
    loading: false,
    rejecting: false,
    printing: false,
    creating: false,
    requesting: false,
    deleting: false,
    updating: false
  }
}

const useQuoteRequest = defineStore('quoteRequest', {
  state: () => initialState(),
  actions: {
    init(quoteRequest?: QuoteRequest) {
      this.quoteRequest = quoteRequest
    },
    async createQuoteRequest(name: string, items: ShortQuoteRequestItem[] = []) {
      this.creating = true

      return api
        .createQuoteRequest({ name, items })
        .then(({ data }) => {
          router.push({
            name: 'Quote',
            params: { id: data }
          })
        })
        .catch(handleApiError)
        .finally(() => (this.creating = false))
    },
    async getQuoteRequest(id: string, backgroundLoading = false) {
      this.loading = !backgroundLoading

      return api
        .getQuoteRequest(id)
        .then(({ data }) => {
          this.quoteRequest = data
        })
        .catch((error: ApiError) => {
          handleApiError(
            error,
            {
              appearance: 'CUSTOM',
              customHandler: 'CategoryLayout'
            },
            {
              errorCode: 404,
              logError: false,
              customMessage: t('quoteRequest.requestNotFound')
            }
          )
        })
        .finally(() => (this.loading = false))
    },
    async updateQuoteRequest(id: string, patch: QuoteRequestPatch) {
      this.updating = true

      return api
        .updateQuoteRequest(id, patch)
        .catch((error) => {
          handleApiError(error, undefined, {
            errorCode: 403,
            logError: false,
            customMessage: t('quoteRequest.cannotEditRequest')
          })

          if (error.code === 403) {
            useQuote().getQuote(id, true)
          }
        })
        .finally(() => (this.updating = false))
    },
    async deleteQuoteRequest(id: string) {
      this.deleting = true

      return api
        .deleteQuoteRequest(id)
        .then(() => router.push({ name: 'Quotes' }))
        .catch((error) => {
          handleApiError(error, undefined, {
            errorCode: 403,
            logError: false,
            customMessage: t('quoteRequest.cannotEditRequest')
          })

          if (error.code === 403) {
            useQuote().getQuote(id, true)
          }
        })
        .finally(() => (this.deleting = false))
    },
    async updateLabel(itemId: string, label: string | undefined) {
      this.updating = true

      if (this.quoteRequest) {
        const requestId = this.quoteRequest.id

        return api
          .updateQuoteRequestItem(this.quoteRequest.id, itemId, {
            comment: label
          })
          .catch((error: ApiError) => {
            handleApiError(
              error,
              {
                customMessage: t('quoteRequest.textLineItemChangeError')
              },
              {
                errorCode: 403,
                logError: false,
                customMessage: t('quoteRequest.cannotEditRequest')
              }
            )

            if (error.code === 403) {
              useQuote().getQuote(requestId, true)
            }
          })
          .finally(() => (this.updating = false))
      }
    },
    async updateRequestItemQuantity(id: string, quantity: number) {
      this.updating = true

      if (this.quoteRequest) {
        const requestId = this.quoteRequest.id
        const update: QuoteRequestItemDto = {
          quantity: quantity
        }

        return api
          .updateQuoteRequestItem(requestId, id, update)
          .then(() => {
            this.getQuoteRequest(requestId, true)
          })
          .catch((error) => {
            handleApiError(error, undefined, {
              errorCode: 403,
              logError: false,
              customMessage: t('quoteRequest.cannotEditRequest')
            })

            if (error.code === 403) {
              useQuote().getQuote(requestId, true)
            }
          })
          .finally(() => (this.updating = false))
      }
    },
    async deleteRequestItem(id: string) {
      if (this.quoteRequest) {
        const requestId = this.quoteRequest.id
        return api
          .deleteRequestItem(requestId, id)
          .then(() => {
            helpers.reportSuccess(t('collection.success.itemRemoved'))
            this.getQuoteRequest(requestId, true)
          })
          .catch((error) => {
            handleApiError(error, undefined, {
              errorCode: 403,
              logError: false,
              customMessage: t('quoteRequest.cannotEditRequest')
            })

            if (error.code === 403) {
              useQuote().getQuote(requestId, true)
            }
          })
      }
    },
    async addLineItem(newLineItem: ShortQuoteRequestItem, reload = false) {
      if (this.quoteRequest) {
        const requestId = this.quoteRequest.id
        return api
          .addToQuoteRequest(this.quoteRequest.id, newLineItem)
          .then(() => {
            helpers.reportSuccess(t('quoteRequest.lineItemAdded'))
            if (reload) {
              this.getQuoteRequest(requestId, true)
            }
          })
          .catch((error) => {
            handleApiError(error, undefined, {
              errorCode: 403,
              logError: false,
              customMessage: t('quoteRequest.cannotEditRequest')
            })

            if (error.code === 403) {
              useQuote().getQuote(requestId, true)
            }
          })
      }
    },
    async sendRequest() {
      if (this.quoteRequest) {
        this.requesting = true
        const requestId = this.quoteRequest.id
        return api
          .sendQuoteRequest(requestId)
          .then(() => {
            helpers.reportSuccess(t('quoteRequest.sentQuoteRequest'))
            const quoteId = useQuote().quote?.id
            if (quoteId) {
              useQuote().getQuote(quoteId, true)
            }
          })
          .catch((error) => {
            handleApiError(error, undefined, {
              errorCode: 403,
              logError: false,
              customMessage: t('quoteRequest.cannotEditRequest')
            })

            if (error.code === 403) {
              useQuote().getQuote(requestId, true)
            }
          })
          .finally(() => (this.requesting = false))
      }
    }
  }
})

export default useQuoteRequest
